import React, { useState, useEffect } from 'react';
import { Spin, message, Button, DatePicker, Input, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseURL } from '../../../../globalUtils/axiosConfig';
import AttachmentContainer from "./AttachmentContainter";
import { ReactComponent as SaveIcon } from "../../../../assets/save-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/delete-icon.svg";
import { ReactComponent as EditIcon } from "../../../../assets/edit-icon.svg";
import moment from 'moment';

const GrvList = ({ 
  recordType = 'site_receipt',
  projectId,
  record,
  onDataChange, // Callback to refresh parent data
  mcPermissions = {}
}) => {
  const navigate = useNavigate();
  const [showInputView, setShowInputView] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [stringInput, setStringInput] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingRecords, setEditingRecords] = useState([]);

  const handleToggleView = () => {
    setShowInputView(!showInputView);
  };

  const handleEditMode = () => {
    setEditMode(true);
    // Initialize editing records with current data
    if (record?.grvData) {
      const editingData = record.grvData.map((grv, index) => ({
        ...grv,
        grvDate: moment(grv.grvDate),
        originalGrvNo: grv.grvNo // Keep track of original for API calls
      }));
      
      setEditingRecords(editingData);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingRecords([]);
  };

  const handleSaveEdit = async () => {
    try {
      // Validate all records before updating
      const invalidRecords = editingRecords.filter(record => 
        !record.grvNo?.trim() || !record.grvDate
      );
      
      if (invalidRecords.length > 0) {
        message.warning('Please fill in all GRV numbers and dates before saving.');
        return;
      }

      // Check for duplicate GRV numbers within the editing records
      const grvNumbers = editingRecords.map(record => record.grvNo.trim());
      const duplicates = grvNumbers.filter((item, index) => grvNumbers.indexOf(item) !== index);
      
      if (duplicates.length > 0) {
        message.warning(`Duplicate GRV numbers found: ${duplicates.join(', ')}. Please ensure all GRV numbers are unique.`);
        return;
      }

      // Show loading message for multiple updates
      const loadingMessage = message.loading(`Updating ${editingRecords.length} GRV record(s)...`, 0);

      // Update records sequentially instead of in parallel to avoid conflicts
      const results = [];
      
      for (let index = 0; index < editingRecords.length; index++) {
        const editedRecord = editingRecords[index];
        
        try {
          const updateData = {
            oldGrvNo: editedRecord.originalGrvNo,
            newGrvNo: editedRecord.grvNo.trim(),
            grvDate: editedRecord.grvDate.format('YYYY-MM-DD'),
            progressStepId: record.id
          };

          const response = await axios.patch(`${baseURL}/grv`, updateData);
          
          results.push({ success: true, index, grvNo: editedRecord.grvNo, response });
        } catch (error) {
          results.push({ success: false, index, grvNo: editedRecord.grvNo, error });
        }
        
        // Small delay between requests to ensure they don't conflict
        if (index < editingRecords.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // Hide loading message
      loadingMessage();

      // Check results
      const successful = results.filter(result => result.success);
      const failed = results.filter(result => !result.success);

      if (failed.length === 0) {
        // All updates successful
        message.success(`All ${successful.length} GRV record(s) updated successfully!`);
        setEditMode(false);
        setEditingRecords([]);
        // Refresh parent data
        if (onDataChange) {
          onDataChange();
        }
      } else if (successful.length > 0) {
        // Partial success
        message.warning(`${successful.length} record(s) updated successfully, ${failed.length} failed. Please check and retry failed records.`);
        console.error('Failed updates:', failed);
        // Refresh parent data for successful updates
        if (onDataChange) {
          onDataChange();
        }
      } else {
        // All failed
        message.error('Failed to update GRV records. Please try again.');
        console.error('All updates failed:', failed);
      }
      
    } catch (error) {
      console.error('Error updating GRV records:', error);
      message.error('Failed to update GRV records. Please try again.');
    }
  };

  const handleEditRecordChange = (index, field, value) => {
    const updatedRecords = [...editingRecords];
    updatedRecords[index] = {
      ...updatedRecords[index],
      [field]: value
    };
    setEditingRecords(updatedRecords);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleStringInputChange = (e) => {
    setStringInput(e.target.value);
  };

  const handleSaveInputs = async () => {
    if (selectedDate && stringInput.trim()) {
      try {
        const grvData = {
          grvNo: stringInput.trim(),
          grvDate: selectedDate.format('YYYY-MM-DD'),
          progressStepIds: [record.id]
        };

        const response = await axios.post(`${baseURL}/grv`, grvData);
        
        if (response.status === 200 || response.status === 201) {
          message.success('GRV saved successfully!');
          // Reset form
          setSelectedDate(null);
          setStringInput('');
          setShowInputView(false);
          // Refresh parent data
          if (onDataChange) {
            onDataChange();
          }
        }
      } catch (error) {
        console.error('Error saving GRV:', error);
        message.error('Failed to save GRV. Please try again.');
      }
    } else {
      message.warning('Please fill in both date and GRV number');
    }
  };

  const handleDeleteGrv = async (grvNo) => {
    try {
      const deleteData = {
        grvNo: grvNo,
        progressStepId: record.id
      };

      const response = await axios.delete(`${baseURL}/grv`, {
        data: deleteData
      });
      
      if (response.status === 200 || response.status === 204) {
        message.success('GRV deleted successfully!');
        // Refresh parent data
        if (onDataChange) {
          onDataChange();
        }
      }
    } catch (error) {
      console.error('Error deleting GRV:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete GRV. Please try again.';
      message.error(errorMessage);
    }
  };

  return (
    <div className="delivery-notes-list">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '8px' 
      }}>
        <div className="field-label" style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
          GRV #
        </div>
        <div>
          {record?.grvData?.length > 0 && !showInputView && !editMode && mcPermissions?.site_receipt?.add_edit_grv &&(
            <EditIcon
              onClick={handleEditMode}
              style={{
                color: '#f5a44d',
                fill: '#f5a44d',
                cursor: 'pointer',
                width: '14px',
                height: '14px',
                marginRight: '8px'
              }}
            />
          )}
          {editMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={handleCancelEdit}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f5a44d',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <SaveIcon
                onClick={handleSaveEdit}
                style={{
                  color: '#f5a44d',
                  fill: '#f5a44d',
                  cursor: 'pointer',
                  width: '14px',
                  height: '14px'
                }}
              />
            </div>
          )}
          {showInputView && !record?.grvData?.length && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={() => {
                  setShowInputView(false);
                  setSelectedDate(null);
                  setStringInput('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#f5a44d',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '26px',
                  height: '26px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <SaveIcon
                onClick={handleSaveInputs}
                style={{
                  color: '#f5a44d',
                  fill: '#f5a44d',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </div>
          )}
        </div>
      </div>
      {/* Display existing GRV data */}
      {record && record.grvData && record.grvData.length > 0 && !editMode && (
        <div style={{ marginBottom: '8px' }}>
          {record.grvData.map((grv, index) => (
            <div key={index} style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12px', 
              color: '#000', 
              marginBottom: '4px',
              padding: '4px 0',
              width: '100%'
            }}>
              <span style={{ flex: 1, textAlign: 'left' }}>
                {moment(grv.grvDate).format('DD MMM YY')} - {grv.grvNo}
              </span>
              {mcPermissions?.site_receipt?.add_edit_grv && (
                <Popconfirm
                  title="Are you sure you want to delete this GRV?"
                  placement="topRight"
                  onConfirm={() => handleDeleteGrv(grv.grvNo)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteIcon
                    style={{
                      color: '#f5a44d',
                      fill: '#f5a44d',
                      cursor: 'pointer',
                      width: '12px',
                      height: '12px',
                      flexShrink: 0,
                      marginLeft: '40px',
                      marginRight: '8px'
                    }}
                  />
                </Popconfirm>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editable GRV data */}
      {editMode && editingRecords.length > 0 && (
        <div>
          {editingRecords.map((grv, index) => (
            <div key={index}>
              <div style={{ marginBottom: '12px' }}>
                <DatePicker 
                  value={grv.grvDate}
                  onChange={(date) => handleEditRecordChange(index, 'grvDate', date)}
                  placeholder="[Today's Date]"
                  style={{ 
                    width: '100%',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    backgroundColor: '#fafafa',
                    boxSizing: 'border-box'
                  }}
                  suffixIcon={null}
                  format="DD MMM YYYY"
                />
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <Input 
                  value={grv.grvNo}
                  onChange={(e) => handleEditRecordChange(index, 'grvNo', e.target.value)}
                  placeholder=" GRV #"
                  style={{ 
                    width: '100%',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save and close icons when there are existing records */}
      {showInputView && record?.grvData?.length > 0 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '12px'
        }}>
          <button
            onClick={() => {
              setShowInputView(false);
              setSelectedDate(null);
              setStringInput('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#f5a44d',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '26px',
              height: '26px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
          <SaveIcon
            onClick={handleSaveInputs}
            style={{
              color: '#f5a44d',
              fill: '#f5a44d',
              cursor: 'pointer',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </div>
      )}

      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-start' }}>
        {!showInputView && record && mcPermissions?.site_receipt?.add_edit_grv && (
          <Button 
            onClick={handleToggleView}
            style={{ 
              marginBottom: '8px',
              border: '1px solid #f5a44d',
              color: '#f5a44d',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '32px',
              width: '40px',
              borderRadius: '4px'
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>+</span>
          </Button>
        )}
      </div>

      {showInputView ? (
        <div>
            <div style={{ marginBottom: '12px' }}>
              <DatePicker 
                value={selectedDate}
                onChange={handleDateChange}
                placeholder="[Today's Date]"
                style={{ 
                  width: '100%',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  backgroundColor: '#fafafa',
                  boxSizing: 'border-box'
                }}
                suffixIcon={null}
                format="DD MMM YYYY"
              />
            </div>
            
            <div>
              <Input 
                value={stringInput}
                onChange={handleStringInputChange}
                placeholder=" GRV #"
                style={{ 
                  width: '100%',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
        </div>
      ) : (
        <div>
          {/* List view content will go here */}
        </div>
      )}
      
    </div>
  );
};

export default GrvList;
